<template>
  <div>
    <form @submit.prevent="submit">
      <div class="row">
        <div class="col-12">
          <h5 class="text-muted">
            <i class="fas fa-plus text-cr-red-black"></i>
            {{ $t('sentence.create_question') }}
          </h5>
        </div>
        <div
          class="col-lg-6"
          v-for="renderType in renderTypes"
          :key="renderType"
        >
          <label
            class="badge badge-light clickable question-type w-100 mb-1"
            :class="{'bg-cr-red-black text-white': renderType === formData.render_type}"
            :id="`render-type-${renderType}`"
          >
            <input
              v-show="false"
              type="radio"
              :name="`render-type-${renderType}`"
              :value="renderType"
              v-model="formData.render_type"
            />
            <i class="mr-2" :class="[RENDER_TYPE_ICONS[renderType]]"></i>
            {{ $t(RENDER_TYPES_DISPLAY[renderType]) }}
          </label>
        </div>
      </div>
      <hr v-if="formData.render_type"/>
      <transition name="fade-nl">
        <div class="row" v-if="formData.render_type">
          <div class="col-lg-7">
            <div class="mb-2" v-if="formData.render_type !== 'media'">
              <v-text-input
                id="item-add-title"
                :required="true"
                v-model="formData.title"
                label="word.question"
                placeholder="phrase.write_question"
                :error="errors.title"
                :auto-focus="true"
                size="lg"
              />
            </div>

            <div class="mb-2" v-if="formData.render_type !== 'media'">
              <v-text-input
                id="item-add-help-text"
                v-model="formData.help_text"
                label="word.help_text"
                placeholder="sentence.help_text_explanation"
                :errors="errors.help_text"
              />
            </div>
            <div class="mb-2">
              <v-item-config-stars
                v-if="formData.render_type === 'stars'"
                :form-data="formData"
              />
              <v-item-config-choice
                v-else-if="isChoice(formData.render_type)"
                :form-data="formData"
                :render-type="formData.render_type"
              />
              <v-item-media-config
                v-else-if="formData.render_type === 'media'"
                :form-data="formData"
                :errors="errors"
              />
            </div>
          </div>
          <div class="col-lg-5">
            <div class="card bg-light">
              <div class="card-header text-center">
                <h4 class="p-0 m-0">
                  <i class="fa fa-lightbulb mr-2"></i>
                  {{ $t('word.example') }}
                </h4>
              </div>
              <transition name="fade-nl">
                <div class="p-2" v-if="formData.title">
                  <v-item
                    :item="item"
                    :organisation-name="org.name"
                  />
                  <div class="d-flex flex-column align-items-end">
                    <v-button
                      id="item-add-submit"
                      text="word.add"
                      icon="plus"
                      style-type="primary"
                      type="submit"
                    />
                  </div>
                  <div
                    class="mt-2"
                    v-if="errors.non_field_errors && errors.non_field_errors.length > 0"
                  >
                    <v-form-errors
                      :errors="errors.non_field_errors"
                    />
                  </div>
                </div>
              </transition>
              <div v-if="!formData.title" class="card-body text-center">
                {{ $t('sentence.write_title_to_see_example') }}
              </div>
            </div>
          </div>
        </div>
      </transition>
    </form>
    <div v-if="!formData.render_type">
      <hr/>
      <h5 class="text-muted">
        <i class="fas fa-search text-cr-red-black"></i>
        {{ $t('sentence.or_add_an_existing_question') }}
      </h5>
      <v-item-search
        :organisation="organisation"
        :research-type-slug="researchTypeSlug"
        @selected="(id) => $emit('selected', id)"
      />
    </div>
  </div>
</template>

<script>
import TextInput from '@/common/components/inputs/TextInput.vue';
import Button from '@/common/components/Button.vue';
import ItemConfigStars from '@/organisation/components/items/ItemConfigStars.vue';
import ItemConfigChoice from '@/organisation/components/items/ItemConfigChoice.vue';
import ItemConfigMedia from '@/organisation/components/items/ItemConfigMedia.vue';
import { createItem } from '@/organisation/api';
import { isChoice } from '@/common/services/items';
import {
  RENDER_TYPES_DISPLAY,
  RENDER_TYPE_ICONS,
} from '@/organisation/constants';
import ItemSearch from '@/organisation/components/items/ItemSearch.vue';
import Item from '@/organisation/components/research/Item.vue';
import { mapGetters } from 'vuex';
import { copyObject } from '@/common/utils/objects';
import FormErrors from '@/common/components/FormErrors.vue';

export default {
  name: 'ItemAddContainer',
  props: {
    researchTypeSlug: {
      type: String,
    },
    organisation: {
      type: Object,
    },
  },
  components: {
    'v-button': Button,
    'v-form-errors': FormErrors,
    'v-item': Item,
    'v-item-config-choice': ItemConfigChoice,
    'v-item-media-config': ItemConfigMedia,
    'v-item-config-stars': ItemConfigStars,
    'v-item-search': ItemSearch,
    'v-text-input': TextInput,
  },
  data() {
    return {
      RENDER_TYPES_DISPLAY,
      RENDER_TYPE_ICONS,
      searchMode: true,
      mediaType: '',
      item: null,
      formData: {
        title: '',
        render_type: '',
        help_text: '',
        youtube_id: '',
        media_file: null,
        options: [],
        config: {
          direction: 'horizontal',
          allow_other: false,
          exclusive_response: '',
          random: false,
          min_options: null,
          max_options: null,

          // Stars
          min_text: '',
          max_text: '',
        },
      },
      errors: {},
    };
  },
  computed: {
    ...mapGetters(['org', 'user']),
    renderTypes() {
      return Object.keys(RENDER_TYPES_DISPLAY)
        .filter((key) => !['no_field'].includes(key));
    },
  },
  watch: {
    formData: {
      handler(value) {
        const item = copyObject(value);
        if (value.media_file) {
          item.media_file = value.media_file.url;
        }
        this.item = item;
      },
      deep: true,
    },
  },
  methods: {
    isChoice,
    makeFormData() {
      const formData = new FormData();
      formData.append('title', this.formData.title);
      formData.append('render_type', this.formData.render_type);
      formData.append('help_text', this.formData.help_text);
      if (this.formData.youtube_id) {
        formData.append('youtube_id', this.formData.youtube_id);
      }
      if (this.formData.media_file) {
        formData.append('media_file', this.formData.media_file);
      }
      formData.append('organisation_slug', this.organisation.slug);

      if (!this.formData.config.exclusive_response) {
        delete this.formData.config.exclusive_response;
      }
      formData.append('config', JSON.stringify(this.formData.config));
      if (this.formData.options) {
        formData.append('options', JSON.stringify(this.formData.options));
      }
      return formData;
    },
    async submit() {
      const response = await createItem(this.makeFormData());
      if (response.status === 400) {
        this.errors = response.data;
        return;
      }
      this.$emit('created', response.data.id);
    },
  },
};
</script>

<style scoped>
.question-type {
  cursor: pointer;
  padding: 10px;
  font-size: 15px;
}

.question-type:hover {
  background-color: rgba(143, 50, 55, 0.8);
}
</style>
